import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../utils/api";

export type Coupon = {
  code: string;
  discount_percentage: number;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  is_available: boolean;
  num_of_uses: number;
  is_deleted: string | null;
  deleted_by_cascade: boolean;
  created_at: string;
  updated_at: string;
};

export function useCoupons() {
  return useQuery<Coupon[]>({
    queryKey: ["coupons"],
    queryFn: () => fetcher("/store/coupons/"),
  });
}

export function useCoupon(code: string) {
  return useQuery<Coupon>({
    queryKey: ["coupons", code],
    queryFn: () => fetcher(`/store/coupons/${code}/`),
    enabled: !!code,
    retry: false,
  });
}

// 🔥 Hook محسن للبحث عن الكوبون بطريقة case-insensitive
export function useCouponSmart(code: string) {
  const trimmedCode = code?.trim();

  // جرب الكود كما هو أولاً
  const originalQuery = useQuery<Coupon>({
    queryKey: ["coupons", "original", trimmedCode],
    queryFn: () => {
      return fetcher(`/store/coupons/${trimmedCode}/`);
    },
    enabled: !!trimmedCode,
    retry: false,
  });

  // إذا فشل، جرب بحروف كبيرة (فقط إذا كان الكود مختلف عن النسخة الأصلية)
  const shouldTryUppercase =
    !!trimmedCode &&
    originalQuery.isError &&
    trimmedCode !== trimmedCode.toUpperCase();

  const uppercaseQuery = useQuery<Coupon>({
    queryKey: ["coupons", "uppercase", trimmedCode?.toUpperCase()],
    queryFn: () => {
      return fetcher(`/store/coupons/${trimmedCode.toUpperCase()}/`);
    },
    enabled: shouldTryUppercase,
    retry: false,
  });

  // إذا فشل أيضاً، جرب بحروف صغيرة (فقط إذا كان مختلف عن الأصلي والكبير)
  const shouldTryLowercase =
    !!trimmedCode &&
    originalQuery.isError &&
    uppercaseQuery.isError &&
    trimmedCode !== trimmedCode.toLowerCase() &&
    trimmedCode.toUpperCase() !== trimmedCode.toLowerCase();

  const lowercaseQuery = useQuery<Coupon>({
    queryKey: ["coupons", "lowercase", trimmedCode?.toLowerCase()],
    queryFn: () => {
      return fetcher(`/store/coupons/${trimmedCode.toLowerCase()}/`);
    },
    enabled: shouldTryLowercase,
    retry: false,
  });

  // تحديد الحالة الحالية
  const isLoading =
    originalQuery.isLoading ||
    (shouldTryUppercase && uppercaseQuery.isLoading) ||
    (shouldTryLowercase && lowercaseQuery.isLoading);

  // إرجاع النتيجة الناجحة
  if (originalQuery.isSuccess) {
    return {
      data: originalQuery.data,
      isSuccess: true,
      isError: false,
      error: null,
      isLoading: false,
    };
  } else if (uppercaseQuery.isSuccess) {
    return {
      data: uppercaseQuery.data,
      isSuccess: true,
      isError: false,
      error: null,
      isLoading: false,
    };
  } else if (lowercaseQuery.isSuccess) {
    return {
      data: lowercaseQuery.data,
      isSuccess: true,
      isError: false,
      error: null,
      isLoading: false,
    };
  } else {
    // إذا فشلت جميع المحاولات
    const allFailed =
      originalQuery.isError &&
      (!shouldTryUppercase || uppercaseQuery.isError) &&
      (!shouldTryLowercase || lowercaseQuery.isError);

    return {
      data: null,
      isSuccess: false,
      isError: allFailed,
      error:
        originalQuery.error || uppercaseQuery.error || lowercaseQuery.error,
      isLoading: isLoading,
    };
  }
}
